import React from 'react'
import { Copy, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'

const SearchBar = ({
  objectId,
  setObjectId,
  onSearch,
  onNew,
  onCopy,
  onDelete,
  onRegister,
  onPrev,
  onNext,
  objectIdAutoFlg,
  hasData,
  objectExists
}) => {
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSearch()
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
      {/* Mobile Layout - Stacked */}
      <div className="block sm:hidden space-y-3">
        {/* ID Input Row */}
        <div className="flex items-center space-x-2">
          <input
            id="object-id-input-mobile"
            type="text"
            value={objectId}
            onChange={(e) => setObjectId(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="ID"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-semibold min-w-0"
            autoComplete="off"
            aria-label="オブジェクトID入力"
            data-testid="object-id-input-mobile"
          />
          <button
            onClick={onSearch}
            className="px-2 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm whitespace-nowrap flex-shrink-0"
            aria-label="呼出し"
            data-testid="search-button-mobile"
          >
            呼出
          </button>
        </div>

        {/* Navigation and Action Buttons Row */}
        <div className="flex items-center justify-between gap-2">
          {/* Navigation Buttons */}
          {hasData && (
            <div className="flex items-center space-x-1 flex-shrink-0">
              <button
                onClick={onPrev}
                className="p-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                title="前のオブジェクト"
                aria-label="前のオブジェクト"
                data-testid="prev-button-mobile"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={onNext}
                className="p-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                title="次のオブジェクト"
                aria-label="次のオブジェクト"
                data-testid="next-button-mobile"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center space-x-1 flex-shrink-0">
            {/* New Object Button (only in auto mode) */}
            {objectIdAutoFlg === 1 && (
              <button
                onClick={onNew}
                className="px-2 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm whitespace-nowrap"
                title="新規作成"
                aria-label="新規作成"
                data-testid="new-button-mobile"
              >
                新規
              </button>
            )}

            {/* Copy Button */}
            {hasData && (
              <button
                onClick={onCopy}
                className="p-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                title="コピー"
                aria-label="コピー"
                data-testid="copy-button-mobile"
              >
                <Copy size={14} />
              </button>
            )}

            {/* Delete Button (only in manual mode) */}
            {objectIdAutoFlg === 0 && hasData && (
              <button
                onClick={onDelete}
                className="p-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                title="削除"
                aria-label="削除"
                data-testid="delete-button-mobile"
              >
                <Trash2 size={14} />
              </button>
            )}

            {/* Register/Update Button */}
            {hasData && (
              <button
                onClick={onRegister}
                className="px-3 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors font-semibold text-sm whitespace-nowrap"
                aria-label={objectExists ? '更新' : '登録'}
                data-testid="register-button-mobile"
              >
                {objectExists ? '更新' : '登録'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Layout - Horizontal */}
      <div className="hidden sm:flex sm:flex-wrap sm:items-center sm:gap-4">
        {/* ID Input */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <input
              id="object-id-input-desktop"
              type="text"
              value={objectId}
              onChange={(e) => setObjectId(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="ID"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-semibold"
              autoComplete="off"
              aria-label="オブジェクトID入力"
              data-testid="object-id-input-desktop"
            />
            <button
              onClick={onSearch}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              aria-label="呼出し"
              data-testid="search-button-desktop"
            >
              呼 出
            </button>
          </div>
        </div>

        {/* Navigation Buttons */}
        {hasData && (
          <div className="flex items-center space-x-2">
            <button
              onClick={onPrev}
              className="px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
              title="前のオブジェクト"
              aria-label="前のオブジェクト"
              data-testid="prev-button-desktop"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={onNext}
              className="px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
              title="次のオブジェクト"
              aria-label="次のオブジェクト"
              data-testid="next-button-desktop"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          {/* New Object Button (only in auto mode) */}
          {objectIdAutoFlg === 1 && (
            <button
              onClick={onNew}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              title="新規作成"
              aria-label="新規作成"
              data-testid="new-button-desktop"
            >
              新 規
            </button>
          )}

          {/* Copy Button */}
          {hasData && (
            <button
              onClick={onCopy}
              className="p-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              title="コピー"
              aria-label="コピー"
              data-testid="copy-button-desktop"
            >
              <Copy size={16} />
            </button>
          )}

          {/* Delete Button (only in manual mode) */}
          {objectIdAutoFlg === 0 && hasData && (
            <button
              onClick={onDelete}
              className="p-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              title="削除"
              aria-label="削除"
              data-testid="delete-button-desktop"
            >
              <Trash2 size={16} />
            </button>
          )}

          {/* Register/Update Button */}
          {hasData && (
            <button
              onClick={onRegister}
              className="px-6 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors font-semibold"
              aria-label={objectExists ? '更新' : '登録'}
              data-testid="register-button-desktop"
            >
              {objectExists ? '更 新' : '登 録'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default SearchBar

